// Epoch 19: Lead Generator Enhancements - Filter Category Component
// File: src/components/filters/FilterCategory.tsx
// Purpose: Individual filter category with multi-select checkboxes and search

import React, { useState, useMemo } from 'react';
import { FilterCategoryProps } from '../../types/filters';

export default function FilterCategory({
  category,
  selectedValues,
  options,
  onToggleOption,
  onClearCategory,
  searchTerm,
  onSearchChange
}: FilterCategoryProps) {
  const [isExpanded, setIsExpanded] = useState(category.defaultExpanded);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!category.searchable || !searchTerm) return options;
    return options.filter(option => 
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm, category.searchable]);

  // Handle select all for filtered options
  const handleSelectAll = () => {
    const allValues = filteredOptions.map(option => option.value);
    allValues.forEach(value => {
      if (!selectedValues.includes(value)) {
        onToggleOption(value);
      }
    });
  };

  // Handle clear all
  const handleClearAll = () => {
    onClearCategory();
  };

  // Calculate selected count for display
  const selectedCount = selectedValues.length;

  return (
    <div className="border-b border-gray-100 pb-4">
      {/* Category Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center space-x-2 text-sm font-medium text-gray-900 hover:text-gray-700 transition-colors"
        >
          <svg 
            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span>{category.label}</span>
          {selectedCount > 0 && (
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
              {selectedCount}
            </span>
          )}
        </button>
        
        {selectedCount > 0 && (
          <button
            onClick={handleClearAll}
            className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="space-y-3">
          {/* Search Box */}
          {category.searchable && (
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={`Search ${category.label.toLowerCase()}...`}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              />
              <svg 
                className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          )}

          {/* Select All / Clear All */}
          <div className="flex items-center space-x-2 text-xs">
            <button
              onClick={handleSelectAll}
              className="text-blue-600 hover:text-blue-800 transition-colors"
            >
              Select All
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={handleClearAll}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              Clear All
            </button>
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto space-y-2">
            {filteredOptions.map((option) => (
              <label
                key={option.value}
                className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => onToggleOption(option.value)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 transition-colors"
                />
                <span className="text-sm text-gray-700 flex-1">
                  {option.label}
                </span>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {option.count.toLocaleString()}
                </span>
              </label>
            ))}
          </div>

          {/* No results message */}
          {filteredOptions.length === 0 && searchTerm && (
            <div className="text-sm text-gray-500 text-center py-4">
              No results found for "{searchTerm}"
            </div>
          )}

          {/* Loading state */}
          {options.length === 0 && (
            <div className="text-sm text-gray-500 text-center py-4">
              Loading options...
            </div>
          )}
        </div>
      )}
    </div>
  );
}